/**
 * Permission gating, driven by route metadata.
 *
 * The requirement lives on the route (`handle: { permission: 'sale.create' }`), so the same
 * declaration drives both access and navigation rendering — a nav item and its route can never
 * disagree about who may use it.
 *
 * It renders 403 rather than redirecting. A redirect to sign-in makes the user think their
 * session died and hand the device to someone else; "you do not have this permission" tells them
 * the truth and who to ask.
 */

import { Outlet, useMatches } from 'react-router';

import { can, selectUser } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';
import { ForbiddenPage } from '@/pages/errors/ForbiddenPage';

export type RouteHandle = {
  /** Every listed permission is required, matching the server's filter. */
  permission?: string | readonly string[];
  /** Shown in the shell's header. Kept here so the route owns its own title. */
  title?: string;
};

export function PermissionRoute() {
  const user = useAppSelector(selectUser);
  const matches = useMatches();

  // Every matched route's requirement applies, not just the leaf's: a permission on a parent
  // must not be escapable by adding a child route that forgets to repeat it.
  const required = matches.flatMap((match) => {
    const handle = match.handle as RouteHandle | undefined;
    if (handle?.permission === undefined) return [];
    return typeof handle.permission === 'string' ? [handle.permission] : [...handle.permission];
  });

  if (required.length > 0 && !can(user, ...required)) {
    return <ForbiddenPage required={required} />;
  }

  return <Outlet />;
}
